import React, { useState } from 'react';
import { X } from '@phosphor-icons/react';
import { createHelpArticle, updateHelpArticle, uploadHelpVideo } from './helpApi';

const labelStyle = { display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' };
const rowStyle = { marginBottom: '10px' };

function initialState(initial) {
  const video = initial?.video;
  return {
    question: initial?.question || '',
    answer: initial?.answer || '',
    category: initial?.category || 'General',
    tags: (initial?.tags || []).join(', '),
    published: initial?.published ?? true,
    order: initial?.order ?? 0,
    videoType: video?.type || 'none',
    videoUrl: video && video.type !== 'upload' ? video.url || '' : '',
  };
}

/**
 * Create or edit a help entry.
 * - helpId set  → update that admin entry
 * - guideId set → first edit of a built-in topic (saved as an admin override)
 * - neither     → brand-new entry
 */
function HelpArticleForm({ initial, helpId, guideId, heading, onClose, onSaved }) {
  const [form, setForm] = useState(() => initialState(initial));
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const existingUpload = initial?.video?.type === 'upload' ? initial.video : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      let video = null;
      let removeVideo = false;
      if (form.videoType === 'upload') {
        if (file) {
          video = await uploadHelpVideo(file, setProgress);
        } else if (existingUpload) {
          video = existingUpload;
        } else {
          throw new Error('Choose a video file to upload.');
        }
      } else if (form.videoType === 'youtube' || form.videoType === 'url') {
        video = { type: form.videoType, url: form.videoUrl.trim() };
      } else {
        removeVideo = Boolean(initial?.video);
      }

      const payload = {
        question: form.question.trim(),
        answer: form.answer.trim(),
        category: form.category.trim() || 'General',
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        published: form.published,
        order: Number(form.order) || 0,
      };

      if (helpId) {
        await updateHelpArticle(helpId, { ...payload, video, remove_video: removeVideo });
      } else {
        await createHelpArticle({ ...payload, video, guide_id: guideId || null });
      }
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to save help entry');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      data-testid="help-article-modal"
    >
      <div style={{ background: 'white', padding: '24px', width: '100%', maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h2 style={{ fontSize: '15px', fontWeight: '600' }}>{heading || (helpId || guideId ? 'Edit Help Topic' : 'Add Help Entry')}</h2>
          <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
            <X size={22} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={rowStyle}>
            <label style={labelStyle}>Question</label>
            <input className="input-field" value={form.question} onChange={set('question')} required minLength={3} />
          </div>
          <div style={rowStyle}>
            <label style={labelStyle}>Answer</label>
            <textarea className="input-field" value={form.answer} onChange={set('answer')} required rows={6} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 100px', gap: '10px', ...rowStyle }}>
            <div>
              <label style={labelStyle}>Category</label>
              <input className="input-field" value={form.category} onChange={set('category')} />
            </div>
            <div>
              <label style={labelStyle}>Tags (comma separated)</label>
              <input className="input-field" value={form.tags} onChange={set('tags')} />
            </div>
            <div>
              <label style={labelStyle}>Order</label>
              <input className="input-field" type="number" value={form.order} onChange={set('order')} />
            </div>
          </div>

          <div style={rowStyle}>
            <label style={labelStyle}>Video</label>
            <select className="input-field" value={form.videoType} onChange={set('videoType')}>
              <option value="none">No video</option>
              <option value="upload">Upload a video file</option>
              <option value="youtube">YouTube link</option>
              <option value="url">Direct video link (.mp4 etc.)</option>
            </select>
          </div>

          {form.videoType === 'upload' && (
            <div style={rowStyle}>
              {existingUpload && !file && (
                <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>
                  A video is already uploaded. Choose a file only to replace it.
                </p>
              )}
              <input
                type="file"
                accept="video/mp4,video/webm,video/ogg,video/quicktime,.m4v"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              {saving && file && (
                <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>Uploading… {progress}%</p>
              )}
            </div>
          )}

          {(form.videoType === 'youtube' || form.videoType === 'url') && (
            <div style={rowStyle}>
              <input
                className="input-field"
                type="url"
                placeholder="https://"
                value={form.videoUrl}
                onChange={set('videoUrl')}
                required
              />
            </div>
          )}

          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', ...rowStyle }}>
            <input type="checkbox" checked={form.published} onChange={set('published')} />
            Published (visible to all users)
          </label>

          {error && <p style={{ fontSize: '12px', color: '#DC2626', marginBottom: '8px' }}>{error}</p>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default HelpArticleForm;
